import { useEffect, useState } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { orderService, productService } from '../services/api';
import type { Order, Product } from '../types';
import { formatCentsUSD } from '../lib/money';

// Reads the per-order access token a guest needs to view their order. Checkout
// passes it via navigation state and also stashes it in sessionStorage so a
// page refresh still works.
function readAccessToken(orderId: string, stateToken: unknown): string | undefined {
  if (typeof stateToken === 'string' && stateToken) return stateToken;
  try {
    return sessionStorage.getItem(`orderAccess:${orderId}`) || undefined;
  } catch {
    return undefined;
  }
}

export function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const orderId = params.get('orderId');
  const { user } = useAuth();

  const [order, setOrder] = useState<Order | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(!!orderId);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }
    let active = true;
    const token = readAccessToken(orderId, (location.state as { guestAccessToken?: string } | null)?.guestAccessToken);

    setLoading(true);
    Promise.all([
      // Read-after-write guard: retry until the order is readable (handles a
      // refresh that lands during a DB consistency window), then fall back.
      orderService.waitForOrderReadable(orderId, token).catch(() => null),
      productService.getAll().catch(() => [] as Product[]),
    ])
      .then(([o, ps]) => {
        if (!active) return;
        setOrder(o);
        setProducts(ps);
      })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [orderId, location.state]);

  const productName = (id: string) => products.find(p => p.id === id)?.name ?? id;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-cream py-12">
      <div className="text-center max-w-lg w-full">
        <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
        <h1 className="text-4xl font-bold text-gray-900 mb-3">Order Confirmed!</h1>
        <p className="text-gray-700 mb-2">Thank you for your purchase.</p>
        {orderId && (
          <p className="text-gray-500 text-sm mb-2 font-mono">Order: {orderId}</p>
        )}
        <p className="text-gray-600 text-sm mb-8">
          {order
            ? <>A confirmation email is on its way to <strong>{order.customerEmail}</strong> with your receipt and tracking updates.</>
            : "You'll receive an email confirmation shortly with tracking info."}
        </p>

        {loading ? (
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-coastal-600 animate-spin" />
            <span className="ml-3 text-gray-500 text-sm">Loading your order…</span>
          </div>
        ) : order ? (
          <Card className="mb-8 text-left">
            <CardContent className="pt-6">
              <h3 className="font-bold text-lg mb-4">Order summary</h3>
              <div className="space-y-2 mb-4">
                {order.items.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-gray-600 truncate mr-2">{productName(it.productId)} × {it.quantity}</span>
                    <span className="font-medium flex-shrink-0">{formatCentsUSD(it.unitPriceCentsUSD * it.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t pt-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span>{formatCentsUSD(order.subtotalCentsUSD)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span className={order.shippingCentsUSD === 0 ? 'text-green-600 font-semibold' : ''}>
                    {order.shippingCentsUSD === 0 ? 'Free' : formatCentsUSD(order.shippingCentsUSD)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax</span>
                  <span>{formatCentsUSD(order.taxCentsUSD)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>{formatCentsUSD(order.totalCentsUSD)}</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t text-sm text-gray-600">
                <p className="font-medium text-gray-700 mb-1">Shipping to</p>
                <p>{order.shipping.name}</p>
                <p>{order.shipping.line1}{order.shipping.line2 ? `, ${order.shipping.line2}` : ''}</p>
                <p>{order.shipping.city}, {order.shipping.state} {order.shipping.zip}</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg p-6 mb-8 text-left shadow-sm">
            <h3 className="font-bold text-lg mb-4">What&apos;s next?</h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>&#10003; Order confirmation email sent</li>
              <li>&#10003; We&apos;ll carefully prepare your order</li>
              <li>&#10003; Shipping confirmation with tracking info</li>
              <li>&#10003; Delivery to your door</li>
            </ul>
          </div>
        )}

        <div className="flex gap-4 justify-center flex-wrap">
          <Link to="/">
            <Button>Back to Home</Button>
          </Link>
          <Link to="/products">
            <Button variant="secondary">Continue Shopping</Button>
          </Link>
          {user && (
            <Link to="/account">
              <Button variant="outline">View Orders</Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
